import { Reveal } from "./Reveal";
import { Phone, Navigation, MapPin, Clock, Car, Coffee } from "lucide-react";
import {
  PHONE_DISPLAY,
  PHONE_RAW,
  ADDRESS,
  MAPS_URL,
  HOURS,
} from "../../data/content";

const MAP_EMBED =
  "https://www.google.com/maps?q=Oty+Synka+1876%2F8,+708+00+Ostrava&output=embed";

export const Kontakt = ({ showCtas = true }) => {
  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-white py-20 md:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-brand-orange mb-3">
            Kontakt
          </p>
          <h2 className="font-heading font-black text-4xl sm:text-5xl text-brand-navy tracking-tight leading-none">
            Přijďte nebo zavolejte
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            Přijďte k nám v Ostravě nebo nám zavolejte. Pomůžeme s klíči,
            autoklíči, zámky i běžnými problémy kolem zabezpečení.
          </p>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {/* LEFT: contact info */}
          <Reveal>
            <div className="space-y-4">
              {/* Phone */}
              <a
                href={PHONE_RAW}
                data-testid="contact-phone"
                className="group flex items-center gap-4 rounded-2xl bg-brand-navy p-5 hover:-translate-y-0.5 transition-transform duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-orange text-white shrink-0">
                  <Phone className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Telefon
                  </p>
                  <p className="font-heading font-bold text-xl text-white">
                    {PHONE_DISPLAY}
                  </p>
                </div>
              </a>

              {/* Address */}
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-address"
                className="group flex items-center gap-4 rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5 hover:ring-brand-orange transition-all duration-300"
              >
                <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-navy text-brand-orange shrink-0">
                  <MapPin className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                    Adresa
                  </p>
                  <p className="font-bold text-brand-navy">{ADDRESS}</p>
                </div>
              </a>

              {/* Hours */}
              <div className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-navy text-brand-orange shrink-0">
                    <Clock className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <p className="font-heading font-bold text-lg text-brand-navy">
                    Otevírací doba
                  </p>
                </div>
                <ul className="divide-y divide-slate-200">
                  {HOURS.map((h) => (
                    <li
                      key={h.d}
                      className="flex items-center justify-between py-2 text-sm"
                    >
                      <span className="text-slate-600">{h.d}</span>
                      <span className="font-bold text-brand-navy">{h.h}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTAs */}
              {showCtas && (
                <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={PHONE_RAW}
                  data-testid="contact-call-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-orange px-6 py-4 text-base font-bold text-white shadow-lg shadow-brand-orange/25 hover:bg-brand-orangeDark hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Phone className="h-5 w-5" strokeWidth={2.6} />
                  Zavolat
                </a>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-navigate-btn"
                  className="flex-1 inline-flex items-center justify-center gap-2.5 rounded-full border-2 border-brand-navy px-6 py-4 text-base font-bold text-brand-navy hover:bg-brand-navy hover:text-white transition-all duration-300"
                >
                  <Navigation className="h-5 w-5" strokeWidth={2.4} />
                  Navigovat
                </a>
                </div>
              )}

              {/* Parking + coffee box */}
              <div
                data-testid="parking-coffee-box"
                className="rounded-2xl bg-brand-navy p-5 grid sm:grid-cols-2 gap-4"
              >
                <div className="flex gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-orange/15 text-brand-orange shrink-0">
                    <Car className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <div>
                    <p className="font-bold text-white text-sm">
                      Parkování u klíčnictví
                    </p>
                    <p className="text-xs text-slate-400 mt-1 leading-snug">
                      Zaparkujete na našem soukromém parkovišti přímo vedle.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand-orange/15 text-brand-orange shrink-0">
                    <Coffee className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <div>
                    <p className="font-bold text-white text-sm">Káva zdarma</p>
                    <p className="text-xs text-slate-400 mt-1 leading-snug">
                      Během čekání vám naladíme kvalitní italskou kávu zdarma.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* RIGHT: map */}
          <Reveal delay={0.1} className="lg:h-full">
            <div className="rounded-2xl overflow-hidden shadow-xl ring-1 ring-slate-200 h-[360px] md:h-[450px] lg:h-full lg:min-h-[520px]">
              <iframe
                title="Mapa – Klíčnictví Ostrava, Oty Synka 1876/8"
                src={MAP_EMBED}
                data-testid="contact-map"
                width="100%"
                height="100%"
                style={{ border: 0, display: "block", width: "100%", height: "100%" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Kontakt;
